import type { ChatMessage } from './session';

/**
 * Local chat history for the meeting demo.
 *
 * The IM product is an external system (documented in `docs/gap-ledger.md`), so the Demo keeps the
 * conversation it already received in this browser and restores it when the same meeting number is
 * joined again. That is a real, local persistence feature — not a server-side message store and not a
 * second network path: it only ever stores messages the SDK's data channel already delivered, and it
 * writes nothing except a capped JSON array under `nimeet.meeting.history.<channel>`.
 *
 * The storage is injected (defaults to `window.localStorage` when available) so the behaviour is
 * testable without a DOM and degrades to "no history" in private mode instead of failing the meeting.
 */

export interface HistoryStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Chat messages kept per meeting number. Old entries are dropped oldest-first. */
export const CHAT_HISTORY_LIMIT = 100;

/** The browser store, or `null` when no usable `localStorage` exists (e.g. blocked site data). */
export function defaultHistoryStorage(): HistoryStorage | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const probe = 'nimeet.meeting.history.probe';
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return window.localStorage;
  } catch {
    // SAFE: storage is an enhancement; a blocked storage area must not break joining.
    return null;
  }
}

export function historyKey(channel: string): string {
  return `nimeet.meeting.history.${channel}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function toStoredMessage(value: unknown): {
  id: string;
  from: number;
  fromName: string;
  text: string;
  at: string;
} | null {
  if (!isRecord(value)) return null;
  const { id, from, fromName, text, at } = value;
  if (typeof id !== 'string' || id.length === 0) return null;
  if (typeof from !== 'number' || !Number.isFinite(from) || from < 0) return null;
  if (typeof fromName !== 'string' || typeof text !== 'string' || typeof at !== 'string')
    return null;
  if (text.length === 0) return null;
  return { id, from, fromName, text, at };
}

/** Restore the capped, validated chat history for [channel]; `[]` for absent/corrupt storage. */
export function loadChatHistory(
  storage: HistoryStorage | null,
  channel: string,
  limit: number = CHAT_HISTORY_LIMIT,
): ChatMessage[] {
  if (!storage || channel.length === 0) return [];
  let parsed: unknown;
  try {
    const raw = storage.getItem(historyKey(channel));
    if (!raw) return [];
    parsed = JSON.parse(raw);
  } catch {
    // SAFE: corrupt/absent history degrades to an empty log; it must never block the join.
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  const messages: ChatMessage[] = [];
  for (const entry of parsed) {
    const stored = toStoredMessage(entry);
    if (!stored) continue;
    messages.push({ ...stored, kind: 'user', restored: true });
  }
  return messages.slice(-Math.max(0, limit));
}

/**
 * Persist the user messages of [messages] for [channel] (system lines are not history), keeping the
 * newest [limit]. Returns the persisted slice so a caller can assert what was written.
 */
export function saveChatHistory(
  storage: HistoryStorage | null,
  channel: string,
  messages: readonly ChatMessage[],
  limit: number = CHAT_HISTORY_LIMIT,
): ChatMessage[] {
  if (!storage || channel.length === 0) return [];
  const seen = new Set<string>();
  const userMessages = messages.filter((message) => {
    if (message.kind !== 'user' || message.text.trim().length === 0) return false;
    if (seen.has(message.id)) return false;
    seen.add(message.id);
    return true;
  });
  const persisted = userMessages.slice(-Math.max(0, limit));
  try {
    storage.setItem(
      historyKey(channel),
      JSON.stringify(
        persisted.map((message) => ({
          id: message.id,
          from: message.from,
          fromName: message.fromName,
          text: message.text,
          at: message.at,
        })),
      ),
    );
  } catch {
    // SAFE: a full/blocked storage area must not break chatting; the in-session log stays intact.
  }
  return persisted;
}

/** Forget the stored history for [channel] (used by the chat panel's clear action). */
export function clearStoredChatHistory(storage: HistoryStorage | null, channel: string): void {
  if (!storage || channel.length === 0) return;
  try {
    storage.removeItem(historyKey(channel));
  } catch {
    // SAFE: clearing is best effort; the UI already dropped the restored messages.
  }
}
